import {
  AREAS,
  AREA_NAMES_PL,
  GROWTH_AREAS,
  SHOP,
  addDays,
  consistency,
  dayKey,
  formNow,
  messLevel,
  itemName,
  levelProgress,
  WEEKDAY_NAMES_PL,
  weekKey,
  weekdayOfKey,
  type DayKey,
  type GameState,
} from "@squire/core";
import { clear, h } from "./dom.js";

const AREA_COLORS: Record<string, string> = {
  body: "var(--a-body)",
  mind: "var(--a-mind)",
  work: "var(--a-work)",
  social: "var(--a-social)",
  home: "var(--a-home)",
};

const TIDY = ["porządek", "lekki bałagan", "bałagan", "duży bałagan"];

/** Top-left HUD: levels with progress, gold (never real money), active days. Updated in place. */
export class Hud {
  readonly el: HTMLDivElement;
  constructor(host: HTMLElement) {
    this.el = h("div", { class: "hud", "aria-label": "Statystyki" });
    host.append(this.el);
  }

  update(s: GameState, now: number): void {
    clear(this.el);
    const form = formNow(s, dayKey(now, s.settings.timezone));
    for (const a of GROWTH_AREAS) {
      const p = levelProgress(s.stats.xp[a]);
      const pct = Math.round((p.into / p.span) * 100);
      // Form shows only when it dropped: the level stays, the current shape is lower for now.
      const f = a === "work" ? 1 : form[a];
      const formPct = Math.round(f * 100);
      this.el.append(
        h(
          "div",
          {
            class: "chip",
            title: `${AREA_NAMES_PL[a]}: ${Math.floor(s.stats.xp[a])} XP${a === "work" ? "" : `, forma ${formPct}% (poziom nie spada, forma wraca szybciej niż za pierwszym razem)`}`,
          },
          h("span", { class: "dot", style: { background: AREA_COLORS[a] } }),
          `${AREA_NAMES_PL[a]} ${p.level}`,
          formPct < 95 ? h("small", { style: { opacity: "0.75" } }, `${formPct}%`) : "",
          h(
            "span",
            { style: { display: "inline-block", width: "2.2em", height: "0.4em", background: "#4b3d6b" } },
            h("span", {
              style: { display: "block", height: "100%", width: `${pct}%`, background: AREA_COLORS[a] },
            }),
          ),
        ),
      );
    }
    // Dom has no level: chores keep the flat tidy (and pay gold).
    const mess = messLevel(s, now);
    this.el.append(
      h(
        "div",
        { class: "chip", title: "Dom: obowiązki dają złoto i porządek, nie poziomy" },
        h("span", { class: "dot", style: { background: AREA_COLORS.home } }),
        `${AREA_NAMES_PL.home}: ${TIDY[Math.min(mess, 3)]}`,
      ),
    );
    this.el.append(h("div", { class: "chip gold" }, `◆ ${s.stats.gold}`));
    const today = dayKey(now, s.settings.timezone);
    this.el.append(
      h(
        "div",
        { class: "chip", title: "Aktywne dni w ostatnim tygodniu" },
        `${consistency(s.recent, today)}/7 dni`,
      ),
    );
  }
}

/** Overlay helper: one at a time, closed by the ✕ button or a tap outside. */
export function overlay(host: HTMLElement, title: string, body: Node, onClose?: () => void): () => void {
  const close = () => {
    el.remove();
    onClose?.();
  };
  const el = h(
    "div",
    { class: "overlay", onclick: (e: Event) => e.target === el && close() },
    h(
      "div",
      { class: "frame" },
      h(
        "div",
        {
          style: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.8em",
          },
        },
        h("h2", {}, title),
        h("button", { class: "ghost", "aria-label": "Zamknij", onclick: close }, "✕"),
      ),
      body,
    ),
  );
  host.append(el);
  return close;
}

/** "Dowód postępu": what you did in the last 7 days, by day. */
export function journalView(s: GameState, now: number): HTMLElement {
  const today = dayKey(now, s.settings.timezone);
  const wrap = h("div", {}, plansView(s, today));
  const from = addDays(today, -6);
  const week = s.recent.filter((a) => a.day >= from);
  const by: Record<string, number> = {};
  for (const a of week) by[a.area] = (by[a.area] ?? 0) + 1;
  wrap.append(
    h(
      "p",
      { class: "pixel", style: { marginTop: "1em" } },
      `W tym tygodniu: ${week.length} rzeczy · ${consistency(s.recent, today)}/7 aktywnych dni`,
    ),
    h(
      "p",
      {},
      s.ratings[weekKey(today)]
        ? `Twoja ocena tygodnia: ${s.ratings[weekKey(today)]}/5`
        : "Ocena tygodnia: jeszcze nie ma (kompan zapyta w niedzielę wieczorem)",
    ),
    h("p", {}, AREAS.map((a) => `${AREA_NAMES_PL[a]}: ${by[a] ?? 0}`).join(" · ")),
  );
  for (let i = 0; i < 7; i++) {
    const d = addDays(today, -i);
    const items = s.recent.filter((a) => a.day === d);
    const label =
      i === 0
        ? "Dziś"
        : i === 1
          ? "Wczoraj"
          : `${WEEKDAY_NAMES_PL[weekdayOfKey(d)]} ${d.slice(8)}.${d.slice(5, 7)}`;
    wrap.append(h("h3", { style: { marginTop: "0.8em" } }, label));
    if (!items.length) wrap.append(h("p", { style: { color: "#8e8a98" } }, "odpoczynek"));
    else
      wrap.append(
        h(
          "ul",
          { style: { margin: "0.2em 0", paddingLeft: "1.2em" } },
          ...items.map((a) =>
            h(
              "li",
              {},
              `${a.text} `,
              h(
                "small",
                { style: { color: AREA_COLORS[a.area] } },
                a.area === "home"
                  ? `${AREA_NAMES_PL.home} +${a.gold} złota`
                  : `${AREA_NAMES_PL[a.area]} +${a.xp}`,
              ),
            ),
          ),
        ),
      );
  }
  return wrap;
}

/** What the player said they would do: open quests (overdue, today, later) and pending reminders. */
function plansView(s: GameState, today: DayKey): HTMLElement {
  const open = s.quests.filter((q) => q.status === "open").sort((a, b) => a.dueDay.localeCompare(b.dueDay));
  const reminders = s.reminders.filter((r) => r.status !== "done").sort((a, b) => a.at - b.at);
  const when = (d: DayKey) =>
    d < today
      ? "z wcześniej"
      : d === today
        ? "dziś"
        : d === addDays(today, 1)
          ? "jutro"
          : `${d.slice(8)}.${d.slice(5, 7)}`;
  const wrap = h("div", {}, h("h3", {}, "Plany i obietnice"));
  if (!open.length && !reminders.length) {
    wrap.append(h("p", { style: { color: "#8e8a98" } }, "Nic nie czeka. Powiedz kompanowi, co planujesz."));
    return wrap;
  }
  wrap.append(
    h(
      "ul",
      { style: { margin: "0.2em 0", paddingLeft: "1.2em" } },
      ...open.map((q) =>
        h(
          "li",
          {},
          `${q.title} `,
          h(
            "small",
            { style: { color: AREA_COLORS[q.area] } },
            `${AREA_NAMES_PL[q.area]} · ${when(q.dueDay)}${q.repeatDays ? ` · co ${q.repeatDays} dni` : ""}`,
          ),
        ),
      ),
      ...reminders.map((r) =>
        h(
          "li",
          {},
          `⏰ ${r.text} `,
          h(
            "small",
            {},
            new Date(r.at).toLocaleString("pl-PL", {
              weekday: "short",
              hour: "2-digit",
              minute: "2-digit",
              timeZone: s.settings.timezone,
            }),
          ),
        ),
      ),
    ),
  );
  return wrap;
}

/** Fridge note with the shopping list (the only list in alpha). */
export function fridgeNote(
  s: GameState,
  onToggle: (id: string) => void,
  onClearDone: () => void,
): HTMLElement {
  const list = h("ul", {});
  for (const item of s.shopping)
    list.append(
      h(
        "li",
        {
          class: item.done ? "done" : "",
          role: "checkbox",
          "aria-checked": String(item.done),
          onclick: () => onToggle(item.id),
        },
        item.text,
      ),
    );
  return h(
    "div",
    { class: "note" },
    h("h2", {}, "Zakupy"),
    s.shopping.length ? list : h("p", {}, "Pusto. Powiedz na przykład: dopisz mleko do zakupów."),
    s.shopping.some((i) => i.done) ? h("button", { onclick: onClearDone }, "Usuń kupione") : null,
  );
}

/** Gold shop (also reachable by voice: "kupmy mu konsolę"). */
export function shopView(s: GameState, onBuy: (id: string) => void): HTMLElement {
  const owned = new Set(s.inventory.map((i) => i.id));
  return h(
    "div",
    {},
    h("p", { class: "pixel" }, `Złoto avatara: ◆ ${s.stats.gold}`),
    h(
      "div",
      { class: "grid2" },
      ...SHOP.flatMap((item) => [
        h("span", {}, `${itemName(item.id)} · ◆ ${item.price}`),
        owned.has(item.id)
          ? h("span", { class: "pixel" }, "w mieszkaniu")
          : h("button", { disabled: s.stats.gold < item.price, onclick: () => onBuy(item.id) }, "Kup"),
      ]),
    ),
  );
}
